import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProxyService } from '../../services/proxy/proxy.service';
import { Global } from '../../classes/global';

@Component({
  selector: 'app-proxy-import-modal',
  templateUrl: './proxy-import-modal.component.html',
  styleUrls: ['./proxy-import-modal.component.scss']
})
export class ProxyImportModalComponent implements OnInit {
  @Output() imported = new EventEmitter<void>();

  lines = '';
  sourceId: string = null;
  startDate: any = null;
  endDate: any = null;
  note = '';
  costUsd: number = null;
  protocol: string = 'SOCKS5';
  fileType: string = null; // 'txt' | 'csv' | 'xlsx' | null (manual input)

  sources: any[] = [];
  warnings: any[] = [];
  validationErrors: string[] = [];
  importedCount = 0;
  showResult = false;

  constructor(
    private proxyService: ProxyService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    public global: Global
  ) {}

  ngOnInit() {
    this.loadSources();
  }

  loadSources() {
    this.proxyService.querySources({ pageSize: 200, pageNumber: 1 }).subscribe(
      (res: any) => { this.sources = res.items || []; }
    );
  }

  onFileChange(event: any) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    this.fileType = ext;

    if (ext === 'xlsx') {
      this.parseXlsx(file);
    } else if (ext === 'csv') {
      this.readFileAsText(file, ';');
    } else {
      this.fileType = 'txt';
      this.readFileAsText(file);
    }
  }

  private readFileAsText(file: File, _separator?: string) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const buffer = e.target.result as ArrayBuffer;
      const bytes = new Uint8Array(buffer);

      if (this.isValidUtf8(bytes)) {
        this.lines = new TextDecoder('utf-8').decode(bytes);
      } else {
        // macOS exports CSV in Mac Roman encoding — use macintosh decoder
        try {
          this.lines = new TextDecoder('macintosh').decode(bytes);
        } catch {
          this.lines = new TextDecoder('windows-1252').decode(bytes);
        }
      }
    };
    reader.readAsArrayBuffer(file);
  }

  private isValidUtf8(bytes: Uint8Array): boolean {
    let i = 0;
    // Skip BOM
    if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
      return true;
    }
    while (i < bytes.length) {
      const b = bytes[i];
      if (b <= 0x7F) { i++; continue; }
      let extra = 0;
      if ((b & 0xE0) === 0xC0) extra = 1;
      else if ((b & 0xF0) === 0xE0) extra = 2;
      else if ((b & 0xF8) === 0xF0) extra = 3;
      else return false; // invalid lead byte
      if (i + extra >= bytes.length) return false;
      for (let j = 1; j <= extra; j++) {
        if ((bytes[i + j] & 0xC0) !== 0x80) return false;
      }
      i += extra + 1;
    }
    return true;
  }

  private parseXlsx(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        // Dynamic import of xlsx library via global script
        const XLSX = (window as any).XLSX;
        if (!XLSX) {
          this.toastr.error('XLSX library not loaded. Use CSV format instead.');
          return;
        }
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Convert to semicolon-separated lines (keep header for backend auto-detect)
        const csvLines: string[] = [];
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length < 3) continue;
          csvLines.push(row.map((c: any) => c ?? '').join(';'));
        }
        this.lines = csvLines.join('\n');
      } catch (err) {
        this.toastr.error('Failed to parse XLSX file');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  doImport() {
    if (!this.lines.trim()) {
      this.toastr.warning('Nhập danh sách proxy');
      return;
    }

    // Validate lines
    this.validationErrors = this.validateLines();
    if (this.validationErrors.length > 0) {
      this.toastr.warning('Có lỗi dữ liệu, vui lòng kiểm tra lại');
      return;
    }

    this.global.pageLoading = true;
    const request = {
      lines: this.lines,
      sourceId: this.sourceId,
      startDate: this.startDate ? new Date(this.startDate).toISOString() : null,
      endDate: this.endDate ? new Date(this.endDate).toISOString() : null,
      note: this.note,
      protocol: this.fileType !== 'csv' && this.fileType !== 'xlsx' ? this.protocol : null,
      totalCost: parseFloat(this.costUsd as any) || 0
    };

    this.proxyService.importProxies(request).subscribe(
      (res: any) => {
        this.global.pageLoading = false;
        this.importedCount = res.importedCount;
        this.warnings = res.warnings || [];
        this.showResult = true;

        if (res.importedCount > 0) {
          this.toastr.success(`Imported ${res.importedCount} proxies`);
          this.imported.emit();
        }
        if (this.warnings.length > 0) {
          this.toastr.warning(`${this.warnings.length} proxies already exist`);
        }
        // Auto-close modal after import
        this.modalService.dismissAll();
      },
      (err: any) => {
        this.global.pageLoading = false;
        this.toastr.error(err.error || 'Import failed');
      }
    );
  }

  close() {
    this.modalService.dismissAll();
  }

  private validateLines(): string[] {
    const errors: string[] = [];
    const text = this.lines.trim();
    if (!text) return errors;

    const lines = text.split(/[\n\r]+/).filter(l => l.trim());
    const isCsv = this.fileType === 'csv' || this.fileType === 'xlsx';

    if (isCsv) {
      // CSV/XLSX: first line is header, validate data rows
      const header = lines[0];
      const sep = header.includes(';') ? ';' : (header.includes(',') ? ',' : ':');
      const cols = header.split(sep).map(c => c.trim().toLowerCase());
      const iIp = cols.findIndex(c => c.includes('ip'));
      const iPortSocks5 = cols.findIndex(c => c.includes('socks5'));
      const iPortHttps = cols.findIndex(c => c.includes('https') || c.includes('http'));
      const iPort = cols.findIndex(c => c === 'port');
      const iUser = cols.findIndex(c => c.includes('user'));
      const iPass = cols.findIndex(c => c.includes('pass'));

      for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(sep);
        const val = (idx: number) => idx >= 0 && idx < parts.length ? parts[idx].trim() : '';
        const host = val(iIp);
        const portS = val(iPortSocks5);
        const portH = val(iPortHttps);
        const portG = val(iPort);
        const user = val(iUser);
        const pass = val(iPass);

        if (!host) { errors.push(`Dòng ${i + 1}: thiếu Host/IP`); continue; }
        if (!portS && !portH && !portG) errors.push(`Dòng ${i + 1} (${host}): thiếu Port (Socks5 hoặc HTTP)`);
        if (!user) errors.push(`Dòng ${i + 1} (${host}): thiếu Username`);
        if (!pass) errors.push(`Dòng ${i + 1} (${host}): thiếu Password`);
      }
    } else {
      // Simple format: host:port:user:pass
      for (let i = 0; i < lines.length; i++) {
        const parts = lines[i].trim().split(/[:;,]/);
        const host = parts[0]?.trim();
        const port = parts[1]?.trim();
        const user = parts[2]?.trim();
        const pass = parts[3]?.trim();

        if (!host) { errors.push(`Dòng ${i + 1}: thiếu Host`); continue; }
        if (!port || isNaN(Number(port))) errors.push(`Dòng ${i + 1} (${host}): thiếu hoặc sai Port`);
        if (!user) errors.push(`Dòng ${i + 1} (${host}): thiếu Username`);
        if (!pass) errors.push(`Dòng ${i + 1} (${host}): thiếu Password`);
      }
    }

    return errors;
  }

  reset() {
    this.lines = '';
    this.sourceId = null;
    this.startDate = null;
    this.endDate = null;
    this.note = '';
    this.costUsd = null;
    this.protocol = 'SOCKS5';
    this.fileType = null;
    this.warnings = [];
    this.validationErrors = [];
    this.importedCount = 0;
    this.showResult = false;
  }
}
